import ApiBase from './ApiBase';
import { FulfillmentIssue } from '../types/fulfillments';
import { ClaimCategory } from '../../../types/common.interface';

export default class ClaimService extends ApiBase {
  constructor(apiBaseUrl: string) {
    super(apiBaseUrl);
  }

  /**
   * Trades the token in a claim email for a guest session. The response interceptor stores the
   * returned `guestSession`, so callers only need to await this before fetching claim data.
   */
  async exchangeClaimLink(token: string) {
    const { data } = await this.client.post(`claims/link/exchange`, { token });

    return data;
  }

  async findClaimSession(orderId) {
    const { data } = await this.client.get(`customer-portal/sessions?orderId=${orderId}`);

    return data;
  }

  async updateClaimSession(body) {
    const { data } = await this.client.put(`customer-portal/sessions`, body);

    return data;
  }

  async deleteClaimSession(id) {
    return this.client.delete(`customer-portal/sessions/${id}`);
  }

  async findClaimById(id) {
    const { data } = await this.client.get(`customer-portal/claims/${id}`);

    return data;
  }

  async findReviews(id) {
    const { data } = await this.client.get(`customer-portal/claims/${id}/reviews`);

    return data;
  }

  async createReview(review, claimId) {
    const { data } = await this.client.post(`customer-portal/claims/${claimId}/reviews`, review);

    return data;
  }

  async createClaimMessage(data, storeId, sourceOrderId) {
    return this.client.post(`customer-portal/claims/${sourceOrderId}/messages?storeId=${storeId}`, data);
  }

  async loadClaimMessages(claimId, sourceOrderId) {
    const { data } = await this.client.get(`customer-portal/claims/${sourceOrderId}/messages?claimId=${claimId}`);

    return data;
  }

  transformStoreToClaim(email, claimInfo) {
    return {
      email,
      fulfillmentIssues: Object.entries(claimInfo.fulfillments).reduce(
        (acc: FulfillmentIssue[], [sourceFulfillmentId, fulfillment]: [string, any]) => {
          acc.push({
            issues: fulfillment.issues.map(({ item, ...issue }) => ({
              ...issue,
            })),
            sourceFulfillmentId,
          });

          return acc;
        },
        [],
      ),
      resolution: {
        customerSignature: claimInfo.signature,
        desiredMethod: claimInfo.resolution.desiredMethod,
        updatedShippingAddress: claimInfo.resolution.updatedShippingAddress,
      },
    };
  }

  async createClaim({
    claim,
    sourceOrderId,
    idempotencyKey,
  }: {
    claim: unknown;
    sourceOrderId: string;
    idempotencyKey?: string;
  }) {
    const config = idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined;
    return this.client.post(`customer-portal/claims/${sourceOrderId}`, claim, config);
  }

  async notifyClaimCustomer(claimId: string) {
    return this.client.post(`customer-portal/claims/${claimId}/notify`);
  }

  async saveCustomerSignature(claimId: string, updateData: { imageUrl: string; address?: unknown }) {
    return this.client.put(`customer-portal/claims/${claimId}/signature`, updateData);
  }

  async getClaimSubtypes(storeId: string, claimCategory: ClaimCategory) {
    return this.client.get(`customer-portal/claims/types/subtypes/${storeId}`, { params: { claimCategory } });
  }

  async createInStoreClaim(claim) {
    return this.client.post(`customer-portal/claims/offline`, claim);
  }

  /**
   * The in-store flow has no order to look up, so it earns its session here instead — at contact info,
   * the first point where there is anything to bind one to. The response interceptor stores the
   * returned session, so every later call in the flow carries it.
   */
  async createInStoreSession({ storeId, email }: { storeId: string; email: string }) {
    return this.client.post(`customer-portal/sessions/in-store`, { storeId, email });
  }
}
